// apps/client/src/utils/drawAgainstCommission.ts
// Draw-against-commission reconciliation logic.
// All monetary values in integer cents. Never use floats.

import { findActiveConfig } from './configResolution';

export interface MonthlyReconciliation {
  month: string;                      // 'YYYY-MM'
  brokerCommsCents: number;           // Broker's commission share for the month
  baseSalaryCents: number;            // Monthly base salary (from point-in-time config)
  totalPaidCents: number;             // MAX(comms, base) — broker always gets at least base
  amtOnTopOfBaseCents: number;        // comms - base (negative = shortfall)
  carryOverCents: number;             // Accumulated shortfall from previous months
  note: string;
}

/**
 * Compute monthly draw-against-commission reconciliation.
 *
 * For each month:
 * - If comms >= base: broker earns comms. Excess reduces accumulated carry-over.
 * - If comms < base: shortfall added to carry-over. Broker still gets base salary.
 *
 * Carry-over accumulates across months. When comms exceed base, the excess
 * first pays down carry-over before counting as "amount on top of base".
 */
export function computeReconciliation(
  monthlyComms: { month: string; cents: number }[],
  salaryHistory: { effectiveFrom: Date; effectiveTo?: Date | null; baseSalaryCents: number }[]
): MonthlyReconciliation[] {
  let carryOver = 0;

  return monthlyComms.map(m => {
    const cfg = findActiveConfig(m.month, salaryHistory);
    const salary = cfg?.baseSalaryCents ?? 0;
    const diff = m.cents - salary;

    if (diff >= 0) {
      // Comms exceed base — pay comms, reduce carry-over with excess
      const excessAvailable = diff;
      const carryReduction = Math.min(Math.abs(carryOver), excessAvailable);
      carryOver = Math.min(0, carryOver + excessAvailable);
      const netExcess = excessAvailable - carryReduction;

      return {
        month: m.month,
        brokerCommsCents: m.cents,
        baseSalaryCents: salary,
        totalPaidCents: m.cents,
        amtOnTopOfBaseCents: netExcess,
        carryOverCents: carryOver,
        note: carryReduction > 0 ? 'Partial carry cleared' : '',
      };
    } else {
      // Shortfall — carry over. Broker still gets base salary (guaranteed minimum).
      carryOver += diff;
      return {
        month: m.month,
        brokerCommsCents: m.cents,
        baseSalaryCents: salary,
        totalPaidCents: salary,
        amtOnTopOfBaseCents: diff,
        carryOverCents: carryOver,
        note: 'Shortfall — carry over to next month',
      };
    }
  });
}
